import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import axios from 'axios';

import {
  MdOutlineAssignment,
  MdOutlineLocalHospital,
  MdOutlineHealthAndSafety,
  MdOutlineWarning,
  MdOutlineChecklist,
  MdOutlineSafetyCheck,
  MdOutlineMedicalServices,
  MdOutlineDescription,
  MdKeyboardArrowRight,
  MdOutlinePrecisionManufacturing,
  MdCheckCircle,
  MdEdit,
  MdRadioButtonUnchecked
} from 'react-icons/md';

export default function PreOperative() {
  const { uhid_no } = useParams();

  const [formStatus, setFormStatus] = useState({
    confirmationPhase: 'not-started',
    consentAnaesthesia: 'not-started',
    preInduction: 'not-started',
    highRiskConsent: 'not-started',
    preOpChecklist: 'not-started',
    safetyChecklist: 'not-started',
    anaestheticEvaluation: 'not-started',
    consentMedical: 'not-started'
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAllFormStatuses();
  }, [uhid_no]);

  const getFormStatus = (apiResponse, formKey) => {
    if (apiResponse.status === 'fulfilled') {
      const data = apiResponse.value.data;
      if (data && Object.keys(data).length > 0) return 'complete';
    }
    const savedForm = sessionStorage.getItem(`preOpForm_${uhid_no}_${formKey}`);
    return savedForm ? 'incomplete' : 'not-started';
  };

  const checkAllFormStatuses = async () => {
    try {
      setLoading(true);

      const responses = await Promise.allSettled([
  axios.get(`${API_BASE_URL}/get_confirmation_phase/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_consent/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_pre_induction_assessment/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_consent_high_risk/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_pre_operative_checklist/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_surgical_safety/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_pre_anaesthetic/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_medical_consent/${uhid_no}`),
  axios.get(`${API_BASE_URL}/get_preop_order/${uhid_no}`)
      ]);

      const newStatus = {
        confirmationPhase: getFormStatus(responses[0], 'confirmationPhase'),
        consentAnaesthesia: getFormStatus(responses[1], 'consentAnaesthesia'),
        preInduction: getFormStatus(responses[2], 'preInduction'),
        highRiskConsent: getFormStatus(responses[3], 'highRiskConsent'),
        preOpChecklist: getFormStatus(responses[4], 'preOpChecklist'),
        safetyChecklist: getFormStatus(responses[5], 'safetyChecklist'),
        anaestheticEvaluation: getFormStatus(responses[6], 'anaestheticEvaluation'),
        consentMedical: getFormStatus(responses[7], 'consentMedical'),
        preOperativeOrders: getFormStatus(responses[8], 'preOperativeOrders')
      };

      setFormStatus(newStatus);
    } catch (error) {
      console.error('Error checking form statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <MdCheckCircle className="w-5 h-5 text-green-500" />;
      case 'incomplete':
        return <MdEdit className="w-5 h-5 text-yellow-500" />;
      default:
        return <MdRadioButtonUnchecked className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'complete':
        return 'Completed - Click to Edit';
      case 'incomplete':
        return 'In Progress - Continue';
      default:
        return 'Not Started';
    }
  };

  const getButtonStyle = (status) => {
    const base = "w-full py-9 text-lg flex items-center justify-between px-6 transition-all duration-200";
    switch (status) {
      case 'complete':
        return `${base} bg-green-600 hover:bg-green-500 text-white`;
      case 'incomplete':
        return `${base} bg-yellow-600 hover:bg-yellow-500 text-white`;
      default:
        return `${base} bg-teal-700 hover:bg-teal-600 text-white`;
    }
  };

  const formItems = [
    {
      key: 'confirmationPhase',
      icon: MdOutlineAssignment,
      title: 'Confirmation Phase',
      description: 'Final verification and approval',
      url: `/confirmationPhase/${uhid_no}`
    },
    {
      key: 'consentAnaesthesia',
      icon: MdOutlineLocalHospital,
      title: 'Consent for Anaesthesia',
      description: 'Anaesthesia procedure agreement',
      url: `/consentForAnaesthesia/${uhid_no}`
    },
    {
      key: 'preInduction',
      icon: MdOutlineHealthAndSafety,
      title: 'Pre Induction Assessment',
      description: 'Pre-anaesthesia evaluation',
      url: `/preInductionAssessment/${uhid_no}`
    },
    {
      key: 'highRiskConsent',
      icon: MdOutlineWarning,
      title: 'Consent for High Risk',
      description: 'High-risk procedure agreement',
      url: `/highRiskConsentForm/${uhid_no}`
    },
    {
      key: 'preOpChecklist',
      icon: MdOutlineChecklist,
      title: 'Pre Operative Checklist',
      description: 'Pre-surgery verification list',
      url: `/preOperativeChecklist/${uhid_no}`
    },
    {
      key: 'safetyChecklist',
      icon: MdOutlineSafetyCheck,
      title: 'Surgical Safety Checklist',
      description: 'Surgery safety protocols',
      url: `/surgicalSafetyChecklist/${uhid_no}`
    },
    {
      key: 'anaestheticEvaluation',
      icon: MdOutlineMedicalServices,
      title: 'Pre-Anaesthetic Evaluation',
      description: 'Anaesthesia readiness assessment',
      url: `/preAnaestheticForm/${uhid_no}`
    },
    {
      key: 'consentMedical',
      icon: MdOutlineDescription,
      title: 'Consent for Medical/Surgical Procedure',
      description: 'Surgical procedure agreement',
      url: `/consentForMedical/${uhid_no}`
    },
    {
      key: 'preOperativeOrders',
      icon: MdOutlinePrecisionManufacturing,
      title: 'Pre Operative Orders',
      description: 'Orders for pre-operative care',
      url: `/preoperativeorders/${uhid_no}`
    },
    {
      key:'AnesthesiaForm',
      icon: MdOutlinePrecisionManufacturing,
      title: 'Anesthesia Form',
      description: 'Form for anesthesia details',
      url: `/preanesthesiaform/${uhid_no}`
    }
  ];

  const completedCount = Object.values(formStatus).filter((status) => status === 'complete').length;
  const totalCount = Object.keys(formStatus).length;

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-teal-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-teal-600">Loading form statuses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-teal-50">
      <div className="w-full max-w-4xl p-8 space-y-8 bg-white rounded-lg shadow-lg">
        <div className="flex flex-col items-center space-y-4">
          <MdOutlinePrecisionManufacturing className="w-20 h-20 text-teal-600" />
          <h1 className="text-2xl font-bold text-center text-teal-800">Pre Operative</h1>
          <p className="text-sm text-gray-600">UHID: {uhid_no}</p>
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Progress</span>
            <span className="text-sm text-gray-600">
              {completedCount}/{totalCount} Complete
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-teal-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(completedCount / totalCount) * 100}%` }}
            ></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formItems.map(item => (
            <Button
              key={item.key}
              onClick={() => window.location.href = item.url}
              className={getButtonStyle(formStatus[item.key])}
              variant="default"
            >
              <div className="flex items-center space-x-4">
                <item.icon className="w-6 h-6" />
                <div className="flex flex-col items-start">
                  <span className="font-medium">{item.title}</span>
                  <span className="text-xs text-teal-100">{item.description}</span>
                  <span className="text-xs mt-1 opacity-80">{getStatusText(formStatus[item.key])}</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {getStatusIcon(formStatus[item.key])}
                <MdKeyboardArrowRight className="w-5 h-5" />
              </div>
            </Button>
          ))}
        </div>

        {/* Legend */}
        <div className="bg-gray-50 p-3 rounded-lg">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Status Legend:</h3>
          <div className="flex flex-col space-y-1 text-xs">
            <div className="flex items-center space-x-2">
              <MdCheckCircle className="w-4 h-4 text-green-500" />
              <span className="text-gray-600">Completed (Click to edit)</span>
            </div>
            <div className="flex items-center space-x-2">
              <MdEdit className="w-4 h-4 text-yellow-500" />
              <span className="text-gray-600">In Progress (Continue form)</span>
            </div>
            <div className="flex items-center space-x-2">
              <MdRadioButtonUnchecked className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Not Started</span>
            </div>
          </div>
        </div>

        {/* Refresh Button */}
        <Button
          onClick={checkAllFormStatuses}
          className="w-full bg-teal-600 hover:bg-teal-700 text-white"
          disabled={loading}
        >
          {loading ? 'Refreshing...' : 'Refresh Status'}
        </Button>
      </div>
    </div>
  );
}

